import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '../services/adminApi';
import './AdminDashboard.css';

// ✅ FIXED
const SERVER_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) return imagePath;
  if (imagePath.startsWith('/uploads/')) return `${SERVER_URL}${imagePath}`;
  return imagePath;
};

const AdminDashboard = () => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [stats, setStats] = useState({
    totalTrips: 0, totalBookings: 0, totalUsers: 0, totalRevenue: 0, recentBookings: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchHeroSlides = useCallback(async () => {
    try {
      const data = await adminApi.getHeroSlides();
      if (data.success && data.slides) {
        setHeroSlides(data.slides.map(slide => ({
          ...slide,
          id: slide._id,
          image: resolveImageUrl(slide.image),
          imageMobile: resolveImageUrl(slide.imageMobile),
        })));
      }
    } catch (error) {
      console.error('Failed to fetch hero slides:', error);
    }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';

      const res = await fetch(`${API_BASE}/bookings/admin/stats`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();

      if (data.success) {
        const s = data.stats;
        setStats({
          totalTrips: 0,
          totalBookings: s.totalBookings,
          totalUsers: s.totalUsers,
          totalRevenue: s.totalRevenue,
          recentBookings: (s.recentBookings || []).map((b) => ({
            id: b._id,
            user: b.contactInfo?.name || b.user?.name || 'Unknown',
            trip: b.tripTitle,
            amount: b.grandTotal,
            status: b.status,
            date: new Date(b.createdAt).toISOString().split('T')[0],
          })),
        });
      }
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setStats({
        totalTrips: 0,
        totalBookings: 0,
        totalUsers: 0,
        totalRevenue: 0,
        recentBookings: [],
      });
    }
  }, []);

  const fetchTripCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';

      const res = await fetch(`${API_BASE}/trips/admin/all`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setStats((prev) =>
          prev ? { ...prev, totalTrips: data.count || data.trips?.length || 0 } : prev
        );
      }
    } catch (err) {
      console.error('Failed to fetch trip count:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      await Promise.all([
        fetchHeroSlides(),
        fetchDashboardStats(),
        fetchTripCount(),
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchHeroSlides, fetchDashboardStats, fetchTripCount]);

  const statCards = [
    {
      title: 'Total Trips', value: stats.totalTrips, change: '+3 this month',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>),
      link: '/trip-details',                    // ✅ CHANGED
    },
    {
      title: 'Total Bookings', value: stats.totalBookings, change: '+12 this week',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
      link: '/bookings',
    },
    {
      title: 'Total Users', value: stats.totalUsers, change: '+48 this month',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
      link: '/users',
    },
    {
      title: 'Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, change: '+18% vs last month',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
      link: '/bookings',
    },
  ];

  const getStatusClass = (status) => {
    switch (status) {
      case 'confirmed': return 'confirmed';
      case 'pending': return 'pending';
      case 'cancelled': return 'cancelled';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="admin-dash">
        <div className="admin-dash__loading">
          <div className="admin-dash__spinner" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dash">
      {/* Welcome */}
      <div className="admin-dash__welcome">
        <div>
          <h1 className="admin-dash__title">Welcome back 👋</h1>
          <p className="admin-dash__subtitle">Here's what's happening with your travel business today.</p>
        </div>
        <Link to="/trip-details" className="admin-dash__btn">           {/* ✅ CHANGED */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add New Trip
        </Link>
      </div>

      {/* Stats */}
      <div className="admin-dash__stats">
        {statCards.map((stat, i) => (
          <Link to={stat.link} key={i} className="admin-dash__stat-card">
            <div className="admin-dash__stat-top">
              <div className="admin-dash__stat-icon">{stat.icon}</div>
              <svg className="admin-dash__stat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="7 17 17 7"/><polyline points="7 7 17 7 17 17"/>
              </svg>
            </div>
            <span className="admin-dash__stat-value">{stat.value}</span>
            <span className="admin-dash__stat-label">{stat.title}</span>
            <div className="admin-dash__stat-change">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
              {stat.change}
            </div>
          </Link>
        ))}
      </div>

      {/* Grid */}
      <div className="admin-dash__grid">
        {/* Recent Bookings */}
        <div className="admin-dash__card admin-dash__card--wide">
          <div className="admin-dash__card-header">
            <div>
              <h2 className="admin-dash__card-title">Recent Bookings</h2>
              <p className="admin-dash__card-desc">Latest customer bookings</p>
            </div>
            <Link to="/bookings" className="admin-dash__card-link">
              View All
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </Link>
          </div>
          <div className="admin-dash__table-wrap">
            <table className="admin-dash__table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Trip</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <div className="admin-dash__customer">
                        <div className="admin-dash__customer-avatar">{booking.user.charAt(0)}</div>
                        <span>{booking.user}</span>
                      </div>
                    </td>
                    <td><span className="admin-dash__trip-name">{booking.trip}</span></td>
                    <td><span className="admin-dash__amount">₹{booking.amount.toLocaleString()}</span></td>
                    <td>
                      <span className={`admin-dash__badge admin-dash__badge--${getStatusClass(booking.status)}`}>
                        <span className="admin-dash__badge-dot" />
                        {booking.status}
                      </span>
                    </td>
                    <td><span className="admin-dash__date">{booking.date}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hero Widget */}
        <div className="admin-dash__card">
          <div className="admin-dash__card-header">
            <div>
              <h2 className="admin-dash__card-title">Hero Section</h2>
              <p className="admin-dash__card-desc">Carousel status</p>
            </div>
            <Link to="/hero" className="admin-dash__card-link">
              Manage
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </Link>
          </div>
          <div className="admin-dash__card-body">
            <div className="admin-dash__hero-metrics">
              <div className="admin-dash__hero-metric">
                <span className="admin-dash__hero-metric-value">{heroSlides.length}</span>
                <span className="admin-dash__hero-metric-label">Total</span>
              </div>
              <div className="admin-dash__hero-metric-divider" />
              <div className="admin-dash__hero-metric">
                <span className="admin-dash__hero-metric-value" style={{ color: 'var(--admin-success)' }}>
                  {heroSlides.filter(s => s.isActive).length}
                </span>
                <span className="admin-dash__hero-metric-label">Active</span>
              </div>
              <div className="admin-dash__hero-metric-divider" />
              <div className="admin-dash__hero-metric">
                <span className="admin-dash__hero-metric-value" style={{ color: 'var(--admin-text-muted)' }}>
                  {heroSlides.filter(s => !s.isActive).length}
                </span>
                <span className="admin-dash__hero-metric-label">Inactive</span>
              </div>
            </div>

            <div className="admin-dash__hero-slides">
              {heroSlides.slice(0, 3).map((slide) => (
                <div key={slide.id} className="admin-dash__hero-slide">
                  <img src={slide.image} alt={slide.title} />
                  <div className="admin-dash__hero-slide-info">
                    <span className="admin-dash__hero-slide-title">{slide.title}</span>
                    <span className={`admin-dash__hero-slide-status ${slide.isActive ? 'active' : 'inactive'}`}>
                      {slide.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
              {heroSlides.length > 3 && (
                <Link to="/hero" className="admin-dash__hero-more">+{heroSlides.length - 3} more</Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-dash__quick-actions">
        <h2 className="admin-dash__section-title">Quick Actions</h2>
        <div className="admin-dash__actions-grid">
          {[
            { label: 'Manage Hero', desc: 'Edit carousel slides', link: '/hero',
              icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>) },
            { label: 'Add Trip', desc: 'Create new package', link: '/trip-details',     // ✅ CHANGED
              icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>) },
            { label: 'View Reports', desc: 'Booking analytics', link: '/bookings',
              icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>) },
            { label: 'Manage Users', desc: 'User accounts', link: '/users',
              icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>) },
          ].map((action, i) => (
            <Link to={action.link} key={i} className="admin-dash__action-card">
              <div className="admin-dash__action-icon">{action.icon}</div>
              <div className="admin-dash__action-text">
                <span className="admin-dash__action-label">{action.label}</span>
                <span className="admin-dash__action-desc">{action.desc}</span>
              </div>
              <svg className="admin-dash__action-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
